import React from "react";
import { Checkbox, InputNumber, Input, Tooltip } from "antd";
import styled from "styled-components";
import { Question, QuestionPart } from "../types";
import QuestionPreviewComponent from "Pages/TestEvaluation/StudentEvaluation/Components/QuestionPreviewComponent";
import { InfoCircleOutlined } from "@ant-design/icons";

const QuestionHeaderContainer = styled.div<{
  status: "full" | "partial" | "none" | "unattempted";
  isChallenged?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  .left {
    display: flex;
    align-items: center;
    gap: 12px;

    .status-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: ${(props) => {
        if (props.isChallenged) {
          return "#ff4d4f";
        }
        switch (props.status) {
          case "full":
            return "#52c41a";
          case "partial":
            return "#faad14";
          case "none":
            return "#f5222d";
          default:
            return "#d9d9d9";
        }
      }};
      box-shadow: 0 0 8px
        ${(props) => {
          if (props.isChallenged) {
            return "rgba(255, 77, 79, 0.4)";
          }
          switch (props.status) {
            case "full":
              return "rgba(82, 196, 26, 0.4)";
            case "partial":
              return "rgba(250, 173, 20, 0.4)";
            case "none":
              return "rgba(245, 34, 45, 0.4)";
            default:
              return "transparent";
          }
        }};
    }

    .q-id {
      font-weight: 800;
      color: ${(props) => (props.isChallenged ? "#ff4d4f" : "#1890ff")};
      font-size: 14px;
      min-width: 24px;
      @media (max-width: 768px) {
        font-size: 12px;
      }
    }

    .q-tag {
      color: #8c8c8c;
      font-size: 10px;
      font-weight: 600;
      background: #f5f5f5;
      padding: 2px 6px;
      border-radius: 4px;
      @media (max-width: 768px) {
        font-size: 9px;
      }
    }
  }

  .marks-info {
    font-weight: 600;
    color: ${(props) => (props.isChallenged ? "#ff4d4f" : "#595959")};
    @media (max-width: 768px) {
      font-size: 12px;
    }
  }
`;

interface QuestionHeaderProps {
  question: Question;
  status: "full" | "partial" | "none" | "unattempted";
  prevQuestion: Question;
  isParent: boolean;
}

export const QuestionHeader: React.FC<QuestionHeaderProps> = ({
  question,
  status,
  isParent,
}) => {
  const isChallenged = question.status?.trim() === "CHALLENGED";
  return (
    <QuestionHeaderContainer status={status} isChallenged={isChallenged}>
      <div className="left">
        <div className="status-dot" />
        <span className="q-id">{`Q. ${question.questionNumber}`}</span>
      </div>
      {!isParent && (
        <div className="marks-info">
          {question.obtainedMarks == null
            ? `${question.totalMarks || 0}`
            : `${question.obtainedMarks}/${question.totalMarks || 0}`}
        </div>
      )}
    </QuestionHeaderContainer>
  );
};

const QuestionDetailsContainer = styled.div`
  padding: 0;
  overflow-x: auto;
  .part-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    .part-label {
      color: #595959;
      font-weight: 500;
      font-size: 12px;
      @media (max-width: 768px) {
        font-size: 11px;
      }
    }

    .input-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &.mobile-stacked {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;

      @media (min-width: 769px) {
        flex-direction: row;
        justify-content: space-between;
      }

      .marks-container {
        display: flex;
        width: 100%;
        justify-content: space-between;
        gap: 16px;

        @media (max-width: 480px) {
          flex-direction: column;
          gap: 12px;
        }

        .mark-item {
          flex: 1;
        }
      }
    }
  }

  .comment-section {
    margin-top: 12px;
    width: 100%;
    .label {
      display: block;
      font-size: 11px;
      font-weight: 600;
      color: #8c8c8c;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    textarea {
      border-radius: 6px;
      font-size: 13px;
      @media (max-width: 768px) {
        font-size: 12px;
      }
      &:focus {
        border-color: #1890ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
      }
    }
  }
`;
const StudentRemarksContainer = styled.div`
  background: #fff1f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;

  .remarks-title {
    font-weight: 700;
    color: #cf1322;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }

  .remarks-content {
    font-style: italic;
    color: #434343;
    font-size: 13px;
    line-height: 1.5;
  }
`;

// ... props interfaces unchanged ...

export const QuestionContent: React.FC<any> = ({
  question,
  onMarksChange,
  onAttemptedChange,
  onAIEvaluatedMarksChange,
  onCommentChange,
  isParent,
}) => {
  let questionData = {};
  try {
    questionData = JSON.parse(question.questionContent);
  } catch (e) {
    questionData = { text: question.questionContent };
  }
  const aiRemarks = question["aiRemarks"] || "";
  const isAttempted =
    question.isAttempted == null ? false : !question.isAttempted;
  return (
    <QuestionDetailsContainer>
      {question.studentRemarks && (
        <StudentRemarksContainer>
          <div className="remarks-title">
            {"STUDENT'S RE-EVALUATION REASON"}
          </div>
          <div className="remarks-content">{question.studentRemarks}</div>

          {question?.reEvaluatedComment && (
            <>
              <div className="remarks-title">
                {"FACULTY RE-EVALUATED Comment"}
              </div>
              <div className="remarks-content">
                {question.reEvaluatedComment}
              </div>
            </>
          )}
        </StudentRemarksContainer>
      )}
      {question.parts ? (
        !isParent &&
        question.parts.map((part: QuestionPart) => (
          <div className="part-row" key={part.id}>
            <span className="part-label">{part.label}</span>
            <div className="input-wrapper">
              <InputNumber
                value={part.marks}
                min={0}
                max={part.total}
                size="small"
                onChange={(val) => onMarksChange(question.id, part.id, val)}
              />
              <span style={{ color: "#bfbfbf" }}>/{part.total}</span>
            </div>
          </div>
        ))
      ) : (
        <div className="part-row mobile-stacked d-flex flex-column align-items-start gap-12">
          {/* Call Question Preview Component here */}
          <QuestionPreviewComponent questionData={questionData} />

          {!isParent && (
            <>
              <div className="marks-container">
                <div className="mark-item">
                  <span className="part-label">Faculty Mark</span>
                  <div className="input-wrapper">
                    <InputNumber
                      value={question.obtainedMarks}
                      min={0}
                      max={question.totalMarks}
                      style={{ width: "100%" }}
                      size="middle"
                      disabled={isAttempted}
                      onChange={(val) => onMarksChange(question.id, null, val)}
                    />
                    <span style={{ color: "#bfbfbf" }}>
                      /{question.totalMarks}
                    </span>
                  </div>
                </div>

                <div className="mark-item">
                  <span className="part-label">AI Mark</span>
                  <Tooltip title={aiRemarks || "No AI remarks available"}>
                    <InfoCircleOutlined
                      style={{
                        color: aiRemarks ? "#1890ff" : "#bfbfbf",
                        marginLeft: 4,
                      }}
                    />
                  </Tooltip>
                  <div className="input-wrapper">
                    <InputNumber
                      value={question.aiEvaluatedMarks}
                      min={0}
                      max={question.totalMarks}
                      disabled={true}
                      style={{ width: "100%" }}
                      size="middle"
                      onChange={(val) =>
                        onAIEvaluatedMarksChange(question.id, null, val)
                      }
                    />
                    <span style={{ color: "#bfbfbf" }}>
                      /{question.totalMarks}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-2">
                <div className="input-wrapper">
                  <Checkbox
                    checked={isAttempted}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onMarksChange(question.id, null, 0);
                      }
                      onAttemptedChange(question.id, !e.target.checked);
                    }}
                  >
                    <span style={{ fontSize: "12px", fontWeight: 500 }}>
                      Not Attempted
                    </span>
                  </Checkbox>
                </div>
              </div>

              <div className="comment-section">
                <span className="label">Add Question Comment</span>
                <Input.TextArea
                  value={question.evaluatedComment || ""}
                  placeholder="..."
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  onChange={(e) =>
                    onCommentChange(question.id, null, e.target.value)
                  }
                />
              </div>
            </>
          )}
        </div>
      )}
    </QuestionDetailsContainer>
  );
};
